import { Button } from '@1trading/react';
import { Building2, Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { PageHeader } from '@/components/layout/page-header';
import { buildJurisdictionColumns } from '@/features/jurisdictions/jurisdiction-columns';
import { applyJurisdictionFilters, JurisdictionFilterBar, type JurisdictionFilterState } from '@/features/jurisdictions/jurisdiction-filter-bar';
import { JurisdictionFormDialog } from '@/features/jurisdictions/jurisdiction-form-dialog';
import { useJurisdictions } from '@/hooks/queries/compliance/use-jurisdictions';
import { getErrorMessage } from '@/lib/api/error';
import { buildJurisdictionsCsv, type Jurisdiction } from '@/services/api/jurisdictions';
import { downloadCsv } from '@/utils/csv';

// Legacy table default page size (options were 10/15/25/50/100)
const PAGE_SIZE = 50;

type DialogState = { mode: 'add' } | { mode: 'edit'; jurisdiction: Jurisdiction } | null;

const JurisdictionsPage = () => {
  const query = useJurisdictions();
  const [filters, setFilters] = useState<JurisdictionFilterState>({});
  const [dialog, setDialog] = useState<DialogState>(null);

  const columns = useMemo(() => buildJurisdictionColumns(), []);
  const jurisdictions = query.data;
  const filteredJurisdictions = useMemo(() => (jurisdictions ? applyJurisdictionFilters(jurisdictions, filters) : undefined), [jurisdictions, filters]);

  const handleDownload = () => {
    if (!jurisdictions) return;
    // Full unfiltered dataset with raw values — never the formatted cells
    downloadCsv(buildJurisdictionsCsv(jurisdictions), `jurisdictions-${new Date().toISOString()}.csv`);
  };

  return (
    <div className="mx-auto flex w-full flex-col gap-4">
      <PageHeader
        actions={
          <>
            <Button className="gap-2" onClick={() => setDialog({ mode: 'add' })} type="button">
              <Building2 className="size-4" />
              Add New Jurisdiction
            </Button>
            <Button className="gap-2" disabled={!jurisdictions} onClick={handleDownload} type="button" variant="outline">
              <Download className="size-4" />
              Download Data
            </Button>
          </>
        }
        title="Jurisdictions"
      />

      {query.isError ? <QueryErrorBanner message={`Failed to load jurisdictions: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} /> : null}

      {/* Table stays mounted during refetches (isPending only covers the initial load) */}
      {!query.isError || jurisdictions ? (
        <>
          <JurisdictionFilterBar data={jurisdictions ?? []} filters={filters} onFiltersChange={setFilters} />
          <DataTable
            columns={columns}
            data={filteredJurisdictions}
            emptyMessage="No results"
            isLoading={query.isPending}
            onRowClick={(row) => setDialog({ mode: 'edit', jurisdiction: row })}
            pageSize={PAGE_SIZE}
          />
        </>
      ) : null}

      {dialog ? <JurisdictionFormDialog jurisdiction={dialog.mode === 'edit' ? dialog.jurisdiction : null} onClose={() => setDialog(null)} /> : null}
    </div>
  );
};
JurisdictionsPage.displayName = 'JurisdictionsPage';

export default JurisdictionsPage;
